import { CombinedMetadata, getRowValue } from '../types';

/*
 * Pure model for SANKEY items: turns the item's table rows (one per flow) into the
 * nodes and links Recharts' <Sankey> draws. Kept apart from the component so the
 * shaping rules are unit-testable.
 *
 * Rows arrive as {Source, Target, Amount, ...}; the column names come from
 * metadata.source_param / target_param / value_param. A party that both pays and is
 * paid by the subject is drawn twice — once on each side — because a Sankey must be
 * acyclic and the two flows are different facts.
 */

export type SankeySide = 'source' | 'subject' | 'target';

export interface SankeyNodeDatum {
    name: string;
    key: string;
    side: SankeySide;
    role: string;
    riskLevel?: string;
    priorAlerts?: number;
    investigatedUnder?: string;
    color: string;
    dashed: boolean;
}

export interface SankeyLinkDatum {
    source: number;
    target: number;
    value: number;
    payments?: number | null;
    share?: string | null;
    channel?: string | null;
    risk?: string | null;
}

export interface SankeyModel {
    nodes: SankeyNodeDatum[];
    links: SankeyLinkDatum[];
    subject: string | null;
    /** Flow into and out of the subject: the bases for a party's share. */
    inTotal: number;
    outTotal: number;
}

export const SANKEY_COLORS = {
    subject: '#f43f5e',
    payer: '#3b82f6',
    payee: '#6366f1',
    other: '#64748b',
    gap: '#475569',
    risk: { critical: '#EF4444', high: '#EF4444', medium: '#F59E0B', low: '#10B981' } as Record<string, string>,
};

export type SankeyPalette = typeof SANKEY_COLORS;

/**
 * On white, amber and emerald fall below 3:1 as marks; a step darker brings them
 * up, and red moves too so it stays distinct from the darker amber.
 */
export const SANKEY_COLORS_LIGHT: SankeyPalette = {
    ...SANKEY_COLORS,
    risk: { critical: '#DC2626', high: '#DC2626', medium: '#CA8A04', low: '#059669' },
};

/** Roles drawn as placeholders (dashed, grey): flows with no identifiable party. */
const PLACEHOLDER_ROLES = new Set(['gap', 'unattributed']);

const toNumber = (value: any): number | null => {
    if (value === null || value === undefined || value === '') return null;
    const n = typeof value === 'number' ? value : Number(String(value).replace(/[,\s]/g, ''));
    return Number.isFinite(n) ? n : null;
};

/** The subject: named in metadata, else the party on the most flows. */
const inferSubject = (pairs: { source: string; target: string }[]): string | null => {
    const counts = new Map<string, number>();
    for (const { source, target } of pairs) {
        counts.set(source, (counts.get(source) || 0) + 1);
        counts.set(target, (counts.get(target) || 0) + 1);
    }
    let best: string | null = null;
    counts.forEach((n, name) => { if (best === null || n > (counts.get(best) || 0)) best = name; });
    return best;
};

const nodeColor = (palette: SankeyPalette, role: string, side: SankeySide, riskLevel?: string): string => {
    if (side === 'subject') return palette.subject;
    if (PLACEHOLDER_ROLES.has(role)) return palette.gap;
    if (role === 'other') return palette.other;
    const risk = riskLevel ? palette.risk[riskLevel.toLowerCase()] : undefined;
    return risk || (side === 'source' ? palette.payer : palette.payee);
};

export const buildSankeyModel = (
    headers: string[],
    rows: any[],
    metadata: CombinedMetadata,
    palette: SankeyPalette = SANKEY_COLORS,
): SankeyModel | null => {
    const sourceKey = metadata.source_param || headers[0] || 'Source';
    const targetKey = metadata.target_param || headers[1] || 'Target';
    const valueKey = metadata.value_param || headers[2] || 'Amount';
    const annotations: Record<string, any> = metadata.node_annotations || {};

    const flows = (Array.isArray(rows) ? rows : [])
        .map(row => ({
            row,
            source: String(getRowValue(row, sourceKey, metadata.source_param, 0) ?? '').trim(),
            target: String(getRowValue(row, targetKey, metadata.target_param, 1) ?? '').trim(),
            value: toNumber(getRowValue(row, valueKey, metadata.value_param, 2)),
        }))
        .filter(f => f.source && f.target && f.source !== f.target && f.value !== null && f.value > 0);
    if (!flows.length) return null;

    const subject: string | null = metadata.subject || inferSubject(flows);
    const nodes: SankeyNodeDatum[] = [];
    const index = new Map<string, number>();

    const nodeFor = (name: string, side: SankeySide): number => {
        const key = side === 'subject' ? 'subject' : `${side}:${name}`;
        const existing = index.get(key);
        if (existing !== undefined) return existing;
        const note = annotations[name] || {};
        const role = side === 'subject' ? 'subject' : String(note.role || (side === 'source' ? 'payer' : 'payee'));
        const riskLevel = note.risk_level ? String(note.risk_level) : undefined;
        nodes.push({
            name, key, side, role, riskLevel,
            priorAlerts: toNumber(note.prior_alerts) ?? undefined,
            investigatedUnder: note.investigated_under ? String(note.investigated_under) : undefined,
            color: nodeColor(palette, role, side, riskLevel),
            dashed: PLACEHOLDER_ROLES.has(role),
        });
        index.set(key, nodes.length - 1);
        return nodes.length - 1;
    };

    const links: SankeyLinkDatum[] = flows.map(({ row, source, target, value }) => {
        const s = nodeFor(source, source === subject ? 'subject' : 'source');
        const t = nodeFor(target, target === subject ? 'subject' : 'target');
        return {
            source: s,
            target: t,
            value: value as number,
            payments: toNumber(getRowValue(row, 'Payments', undefined, 3)),
            share: getRowValue(row, 'Share', undefined, 4),
            channel: getRowValue(row, 'Channel', undefined, 5),
            risk: getRowValue(row, 'Risk', undefined, 6),
        };
    });
    const sumWhere = (keep: (l: SankeyLinkDatum) => boolean) =>
        links.filter(keep).reduce((sum, l) => sum + l.value, 0);
    return {
        nodes, links, subject,
        inTotal: sumWhere(l => nodes[l.target].side === 'subject'),
        outTotal: sumWhere(l => nodes[l.source].side === 'subject'),
    };
};

export const formatAmount = (value: number | null | undefined): string =>
    value === null || value === undefined || !Number.isFinite(value)
        ? 'N/A'
        : new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value);
